import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import GameWrapper from '@/pages/jeux/components/GameWrapper';
import GameOverModal from '@/pages/jeux/components/GameOverModal';
import { useGame } from '@/contexts/GameContext';
import { MEDICAL_WORDS } from '@/mocks/mots-medicaux';
import { HEALTH_TIPS } from '@/mocks/jeux-data';

const MAX_WRONG = 6;

export default function PenduGame() {
  const navigate = useNavigate();
  const { startGame, endGame, gameStatus, gameScore, gameTime, gameErrors, updateScore, updateErrors, unlockAchievement } = useGame();
  const [language, setLanguage] = useState<'fr' | 'en'>('fr');
  const [currentWord, setCurrentWord] = useState('');
  const [currentDef, setCurrentDef] = useState('');
  const [guessed, setGuessed] = useState<Set<string>>(new Set());
  const [wrong, setWrong] = useState<Set<string>>(new Set());
  const [showGameOver, setShowGameOver] = useState(false);
  const [started, setStarted] = useState(false);
  const [wonCount, setWonCount] = useState(0);

  const wrongCount = wrong.size;
  const display = currentWord.split('').map(l => guessed.has(l.toUpperCase()) ? l.toUpperCase() : '_');
  const isWon = currentWord && currentWord.split('').every(l => guessed.has(l.toUpperCase()));
  const isLost = wrongCount >= MAX_WRONG;

  const pickWord = useCallback(() => {
    const words = MEDICAL_WORDS.filter(w => w.language === language);
    const w = words[Math.floor(Math.random() * words.length)];
    setCurrentWord(w.word.toUpperCase());
    setCurrentDef(w.definition);
    setGuessed(new Set());
    setWrong(new Set());
  }, [language]);

  const handleStart = () => {
    pickWord();
    setStarted(true);
    setShowGameOver(false);
    setWonCount(0);
    startGame('pendu');
  };

  const handleGuess = useCallback((letter: string) => {
    if (!started || gameStatus !== 'playing' || isWon || isLost) return;
    if (guessed.has(letter) || wrong.has(letter)) return;

    const upper = letter.toUpperCase();
    if (currentWord.includes(upper)) {
      const newGuessed = new Set(guessed);
      newGuessed.add(upper);
      setGuessed(newGuessed);
    } else {
      const newWrong = new Set(wrong);
      newWrong.add(upper);
      setWrong(newWrong);
      updateErrors(gameErrors + 1);
    }
  }, [started, gameStatus, guessed, wrong, currentWord, isWon, isLost, gameErrors, updateErrors]);

  // Keyboard listener
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (!started || gameStatus !== 'playing') return;
      const key = e.key.toUpperCase();
      if (/^[A-Z]$/.test(key) && key.length === 1) {
        handleGuess(key);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [started, gameStatus, handleGuess]);

  // Check win/lose
  useEffect(() => {
    if (isWon) {
      const bonus = (MAX_WRONG - wrongCount) * 50;
      updateScore(gameScore + 100 + bonus);
      const newWon = wonCount + 1;
      setWonCount(newWon);
      if (newWon >= 10) unlockAchievement('pendu_10');

      setTimeout(() => {
        if (newWon >= 5) {
          endGame(gameScore + 100 + bonus, gameTime, gameErrors);
          setShowGameOver(true);
        } else {
          pickWord();
        }
      }, 1500);
    }
    if (isLost) {
      endGame(gameScore, gameTime, gameErrors);
      setShowGameOver(true);
    }
  }, [isWon, isLost]);

  const handleReplay = () => handleStart();
  const handleQuit = () => { endGame(gameScore, gameTime, gameErrors); navigate('/jeux', { replace: true }); };

  // Draw hangman
  const hangmanParts = [
    <circle key="head" cx="50" cy="25" r="8" stroke="currentColor" strokeWidth="2" fill="none" />,
    <line key="body" x1="50" y1="33" x2="50" y2="55" stroke="currentColor" strokeWidth="2" />,
    <line key="larm" x1="50" y1="40" x2="35" y2="48" stroke="currentColor" strokeWidth="2" />,
    <line key="rarm" x1="50" y1="40" x2="65" y2="48" stroke="currentColor" strokeWidth="2" />,
    <line key="lleg" x1="50" y1="55" x2="40" y2="68" stroke="currentColor" strokeWidth="2" />,
    <line key="rleg" x1="50" y1="55" x2="60" y2="68" stroke="currentColor" strokeWidth="2" />,
  ];

  if (!started) {
    return (
      <GameWrapper gameName="Pendu Médical" onQuit={handleQuit} showPause={false}>
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="bg-background-50 rounded-3xl p-8 max-w-md w-full text-center border border-background-200/70">
            <div className="w-16 h-16 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-translate-2 text-3xl text-accent-600"></i>
            </div>
            <h2 className="text-xl font-bold text-foreground-950 mb-2">Pendu Médical</h2>
            <p className="text-foreground-600 text-sm mb-4">Devine les mots médicaux !</p>
            <div className="flex gap-3 justify-center mb-6">
              <button onClick={() => setLanguage('fr')} className={`px-6 py-2 rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap ${language === 'fr' ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-700'}`}>Français</button>
              <button onClick={() => setLanguage('en')} className={`px-6 py-2 rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap ${language === 'en' ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-700'}`}>English</button>
            </div>
            <button onClick={handleStart} className="w-full py-3 bg-primary-500 text-background-50 rounded-xl font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-play-fill mr-2"></i> Commencer
            </button>
          </div>
        </div>
      </GameWrapper>
    );
  }

  return (
    <GameWrapper gameName="Pendu Médical" onQuit={handleQuit}>
      <div className="flex-1 flex flex-col items-center justify-center p-4">
        <div className="text-sm text-foreground-600 mb-2">Mot {wonCount + 1}/5 · {language === 'fr' ? 'Français' : 'English'}</div>

        {/* Hangman */}
        <svg viewBox="0 0 100 75" className="w-40 h-32 text-foreground-950 mb-4">
          {/* Gallows */}
          <line x1="10" y1="70" x2="90" y2="70" stroke="currentColor" strokeWidth="2" />
          <line x1="30" y1="70" x2="30" y2="5" stroke="currentColor" strokeWidth="2" />
          <line x1="30" y1="5" x2="50" y2="5" stroke="currentColor" strokeWidth="2" />
          <line x1="50" y1="5" x2="50" y2="15" stroke="currentColor" strokeWidth="2" />
          {hangmanParts.slice(0, wrongCount)}
        </svg>

        {/* Word display */}
        <div className="flex gap-1.5 mb-4 flex-wrap justify-center">
          {display.map((ch, i) => (
            <div
              key={i}
              className={`w-7 h-9 border-b-2 flex items-center justify-center text-lg font-bold ${
                ch !== '_' ? 'border-green-500 text-green-700' : 'border-foreground-300 text-foreground-950'
              }`}
            >
              {ch !== '_' ? ch : ''}
            </div>
          ))}
        </div>

        {/* Definition */}
        <p className="text-sm text-foreground-600 text-center mb-4 max-w-md italic">{currentDef}</p>

        {/* Keyboard */}
        <div className="grid grid-cols-9 gap-1 w-full max-w-md">
          {'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map(letter => {
            const isGuessed = guessed.has(letter);
            const isWrongGuess = wrong.has(letter);
            const disabled = isGuessed || isWrongGuess || isWon || isLost;
            return (
              <button
                key={letter}
                onClick={() => handleGuess(letter)}
                disabled={disabled}
                className={`aspect-square rounded-lg text-xs font-bold transition-all cursor-pointer ${
                  isGuessed ? 'bg-green-100 text-green-700' :
                  isWrongGuess ? 'bg-red-100 text-red-500' :
                  'bg-background-100 text-foreground-950 hover:bg-primary-100'
                } ${disabled ? 'opacity-60' : ''}`}
              >
                {letter}
              </button>
            );
          })}
        </div>

        {isWon && (
          <div className="mt-4 bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-semibold animate-in">
            Bravo ! +100 points
          </div>
        )}
      </div>

      {showGameOver && (
        <GameOverModal
          score={gameScore}
          timeElapsed={gameTime}
          errors={gameErrors}
          gameName="pendu"
          message={isLost ? `Le mot était : ${currentWord}` : 'Série terminée !'}
          onReplay={handleReplay}
          onQuit={handleQuit}
          healthTip={HEALTH_TIPS[Math.floor(Math.random() * HEALTH_TIPS.length)]}
        />
      )}
    </GameWrapper>
  );
}